import React from 'react'
import { Tooltip } from '@radix-ui/themes'
import cn from 'clsx'
import { LuFolder } from '@react-icons/all-files/lu/LuFolder'
import { LuInbox } from '@react-icons/all-files/lu/LuInbox'
import { LuSend } from '@react-icons/all-files/lu/LuSend'
import { LuList } from '@react-icons/all-files/lu/LuList'
import { LuUser } from '@react-icons/all-files/lu/LuUser'
import { LuLibrary } from '@react-icons/all-files/lu/LuLibrary'
import { LuDisc3 } from '@react-icons/all-files/lu/LuDisc3'
import { LuFingerprint } from '@react-icons/all-files/lu/LuFingerprint'
import { LuTriangleAlert } from '@react-icons/all-files/lu/LuTriangleAlert'
import { LuChartBar } from '@react-icons/all-files/lu/LuChartBar'
import { LuSearch } from '@react-icons/all-files/lu/LuSearch'
import { LuFilePen } from '@react-icons/all-files/lu/LuFilePen'
import { LuCompass } from '@react-icons/all-files/lu/LuCompass'
import { useSelector } from 'hooks/use-redux'
import { Link } from 'react-router-dom'
import ExpandToggle from './expand'
export const listLink = {
  SIDEBAR_INBOX_LINK: {
    url: '/',
    regexp: '/',
  },
  SIDEBAR_BROWSE_LINK: {
    url: '/',
    regexp: '/',
  },
  SIDEBAR_EMAIL: {
    url: '/campaigns/list',
    regexp: '/campaigns',
  },
  SIDEBAR_LISTS: {
    url: '/lists',
    regexp: '/lists',
  },
  SIDEBAR_CONTACTS: {
    url: '/contacts',
    regexp: '/contacts',
  },
  SIDEBAR_LIBRARIES: {
    url: '/catalogs',
    regexp: '/catalogs',
  },
  SIDEBAR_DISCOVER_MUSIC: {
    url: '/discover',
    regexp: '/discover',
  },
  SIDEBAR_WATERMARKING: {
    url: '/watermarking',
    regexp: '/watermarking',
  },
  SIDEBAR_OPPORTUNITIES: {
    url: '/artist-opportunities',
    regexp: '/artist-opportunities',
  },
  SIDEBAR_INSIGHTS: {
    url: '/insights',
    regexp: '/insights',
  },
  SIDEBAR_SEARCH_PAGE: {
    url: '/search',
    regexp: '/search',
  },
  SIDEBAR_BRIEFS: {
    url: '/briefs',
    regexp: '/briefs',
  },
}
type BaseProps = {
  id: string
  selected?: boolean
  expanded?: boolean
  compact?: boolean
  onClick?: (...args: Array<any>) => any
  nodeRight?: React.ReactNode
}

type Props = BaseProps &
  (
    | { name: string; tooltipLabel?: string }
    | { name: React.ReactNode; tooltipLabel: string }
  )
export default function SidebarSection(
  props: Props,
): React.ReactNode | React.ReactElement<'div'> {
  const location = useSelector(
    state => state.routing?.locationBeforeTransitions || window.location,
  )
  let icon
  let linkSettings
  let expandable = false
  const selected = props.selected || null
  const id = props.id
  const compact = props.compact

  switch (id) {
    case 'CUSTOM_CHANNEL':
      icon = <LuFolder size={16} />
      expandable = true
      break

    case 'SIDEBAR_INBOX':
      icon = <LuInbox size={16} />
      expandable = true
      break

    case 'SIDEBAR_INBOX_LINK':
      icon = <LuInbox size={16} />
      linkSettings = listLink.SIDEBAR_INBOX_LINK
      break

    case 'SIDEBAR_BROWSE_LINK':
      icon = <LuCompass size={16} />
      linkSettings = listLink.SIDEBAR_BROWSE_LINK
      break

    case 'SIDEBAR_EMAIL':
      icon = <LuSend size={16} />
      linkSettings = listLink.SIDEBAR_EMAIL
      break

    case 'SIDEBAR_LISTS':
      icon = <LuList size={16} />
      linkSettings = listLink.SIDEBAR_LISTS
      break

    case 'SIDEBAR_CONTACTS':
      icon = <LuUser size={16} />
      linkSettings = listLink.SIDEBAR_CONTACTS
      break

    case 'SIDEBAR_LIBRARIES':
      icon = <LuLibrary size={16} />
      linkSettings = listLink.SIDEBAR_LIBRARIES
      break

    case 'SIDEBAR_DISCOVER_MUSIC':
      icon = <LuDisc3 size={16} />
      linkSettings = listLink.SIDEBAR_DISCOVER_MUSIC
      break

    case 'SIDEBAR_WATERMARKING':
      icon = <LuFingerprint size={16} />
      linkSettings = listLink.SIDEBAR_WATERMARKING
      break

    case 'SIDEBAR_OPPORTUNITIES':
      icon = <LuTriangleAlert size={16} />
      linkSettings = listLink.SIDEBAR_OPPORTUNITIES
      break

    case 'SIDEBAR_INSIGHTS':
      icon = <LuChartBar size={16} />
      linkSettings = listLink.SIDEBAR_INSIGHTS
      break

    case 'SIDEBAR_SEARCH_PAGE':
      icon = <LuSearch size={16} />
      linkSettings = listLink.SIDEBAR_SEARCH_PAGE
      break

    case 'SIDEBAR_BRIEFS':
      icon = <LuFilePen size={16} />
      linkSettings = listLink.SIDEBAR_BRIEFS
      break
  }

  let item = (
    <>
      <div className="Sidebar_ItemIcon">{icon}</div>
      <div className="Sidebar_Label">{props.name}</div>
      {props.nodeRight}
    </>
  )

  let el: React.ReactElement
  if (linkSettings) {
    el = (
      <Link
        className={cn('Sidebar_BaseItem Sidebar_Section', {
          'Sidebar_Section--Selected': location.pathname.match(
            linkSettings.regexp,
          ),
        })}
        onClick={props.onClick}
        to={linkSettings.url}
      >
        {item}
      </Link>
    )
  } else {
    el = (
      <div
        className="Sidebar_BaseItem Sidebar_Section"
        data-sel={selected}
        onClick={props.onClick}
      >
        {item}
        {expandable && (
          <div className="Sidebar_ToRight">
            <ExpandToggle expanded={props.expanded} />
          </div>
        )}
      </div>
    )
  }

  return compact ? (
    <Tooltip
      className="Sidebar_NavTooltip"
      content={props.tooltipLabel ?? props.name}
      side="right"
    >
      {el}
    </Tooltip>
  ) : (
    el
  )
}
